import { useEffect, useState, useCallback } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Loader2, ShieldCheck, ShieldOff, Copy, Check } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authApi } from "@/api/services";
import type { MfaEnableResponse } from "@/api/types";

type Step = "status" | "scan" | "codes";

interface MfaSetupProps {
  /** "settings" shows the current status + enable/disable; "mandatory" starts
   *  enrolment immediately (login flow where the org requires MFA). */
  mode?: "settings" | "mandatory";
  /** Fired after a successful enable — the response carries session tokens when
   *  this completed a mandatory enrolment (the user had no session yet). */
  onEnabled?: (resp: MfaEnableResponse) => void;
}

function extractDetail(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "detail" in err) return String((err as { detail: unknown }).detail);
  if (err instanceof Error) return err.message;
  return fallback;
}

/** Authenticator-app (TOTP) enrolment + management. Renders the provisioning QR
 *  client-side (the secret never leaves the browser via a third party). */
export function MfaSetup({ mode = "settings", onEnabled }: MfaSetupProps) {
  const mandatory = mode === "mandatory";
  const [step, setStep] = useState<Step>("status");
  const [enabled, setEnabled] = useState<boolean | null>(mandatory ? false : null);
  const [remaining, setRemaining] = useState<number>(0);
  const [secret, setSecret] = useState("");
  const [otpauthUri, setOtpauthUri] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const beginSetup = useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      const resp = await authApi.mfaSetup();
      setSecret(resp.secret);
      setOtpauthUri(resp.otpauth_uri);
      setStep("scan");
    } catch (err) {
      setError(extractDetail(err, "Could not start MFA setup. Please try again."));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (mandatory) {
      void beginSetup();
      return;
    }
    void (async () => {
      try {
        const s = await authApi.mfaStatus();
        setEnabled(s.enabled);
        setRemaining(s.recovery_codes_remaining);
      } catch {
        setEnabled(false);
      }
    })();
  }, [mandatory, beginSetup]);

  async function confirmEnable() {
    setError(null);
    setBusy(true);
    try {
      const resp = await authApi.mfaEnable(code.trim());
      setRecoveryCodes(resp.recovery_codes);
      setEnabled(true);
      setStep("codes");
      onEnabled?.(resp);
    } catch (err) {
      setError(extractDetail(err, "That code wasn't valid. Check your authenticator and try again."));
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    setError(null);
    setBusy(true);
    try {
      await authApi.mfaDisable(password);
      setEnabled(false);
      setPassword("");
      setStep("status");
    } catch (err) {
      setError(extractDetail(err, "Could not disable MFA — is your password correct?"));
    } finally {
      setBusy(false);
    }
  }

  function copySecret() {
    void navigator.clipboard?.writeText(secret).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  }

  // ── Recovery codes (post-enable) ──
  if (step === "codes") {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
          <ShieldCheck className="h-5 w-5" />
          <span className="font-medium">Two-factor authentication is on</span>
        </div>
        <p className="text-sm text-muted-foreground">
          Save these one-time recovery codes somewhere safe. Each works once if you
          lose access to your authenticator. They won't be shown again.
        </p>
        <div className="grid grid-cols-2 gap-2 rounded-md border bg-muted/40 p-3 font-mono text-sm">
          {recoveryCodes.map((c) => <div key={c}>{c}</div>)}
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => navigator.clipboard?.writeText(recoveryCodes.join("\n"))}
        >
          Copy all codes
        </Button>
      </div>
    );
  }

  // ── Scan + confirm ──
  if (step === "scan") {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Scan this QR code with an authenticator app (Google Authenticator, 1Password,
          Authy…), then enter the 6-digit code it shows.
        </p>
        {otpauthUri && (
          <div className="flex justify-center rounded-md bg-white p-4">
            <QRCodeSVG value={otpauthUri} size={176} includeMargin />
          </div>
        )}
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Or enter this key manually</Label>
          <div className="flex items-center gap-2">
            <code className="flex-1 truncate rounded bg-muted px-2 py-1 font-mono text-xs">{secret}</code>
            <Button type="button" variant="ghost" size="sm" onClick={copySecret} aria-label="Copy secret">
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
        </div>
        <div className="space-y-1">
          <Label htmlFor="mfa-confirm-code">6-digit code</Label>
          <Input
            id="mfa-confirm-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\s/g, ""))}
          />
        </div>
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        <div className="flex gap-2">
          <Button type="button" onClick={confirmEnable} disabled={busy || code.trim().length < 6}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Verify &amp; enable
          </Button>
          {!mandatory && (
            <Button type="button" variant="ghost" onClick={() => { setStep("status"); setError(null); }}>
              Cancel
            </Button>
          )}
        </div>
      </div>
    );
  }

  // ── Status (settings) ──
  if (enabled === null) {
    return <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  }

  return (
    <div className="space-y-3">
      {enabled ? (
        <>
          <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
            <ShieldCheck className="h-5 w-5" />
            <span className="font-medium">Two-factor authentication is on</span>
          </div>
          <p className="text-sm text-muted-foreground">
            You have {remaining} recovery code{remaining === 1 ? "" : "s"} remaining. To
            turn off MFA, confirm your password.
          </p>
          <div className="space-y-1">
            <Label htmlFor="mfa-disable-pw">Password</Label>
            <Input
              id="mfa-disable-pw"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your account password"
            />
          </div>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          <Button type="button" variant="destructive" onClick={disable} disabled={busy || !password}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldOff className="mr-2 h-4 w-4" />}
            Turn off two-factor
          </Button>
        </>
      ) : (
        <>
          <div className="flex items-center gap-2 text-muted-foreground">
            <ShieldOff className="h-5 w-5" />
            <span className="font-medium">Two-factor authentication is off</span>
          </div>
          <p className="text-sm text-muted-foreground">
            Add a second factor with an authenticator app so a stolen password isn't
            enough to sign in.
          </p>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          <Button type="button" onClick={beginSetup} disabled={busy}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Set up two-factor authentication
          </Button>
        </>
      )}
    </div>
  );
}
